// frontend/src/pages/referrals/Referrals.jsx

import React, { useEffect, useState, useContext } from "react";
import "./referrals.css";
import { Context } from "../../context/Context";

export default function ReferralDashboard() {
  const { user } = useContext(Context);
  const token =
    user?.token ||
    user?.accessToken ||
    user?.jwt ||
    "";

  const [policy, setPolicy] = useState(null);
  const cur = policy?.currency || "CAD";

  const [promoCode, setPromoCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ totalEarnings: 0, paidOut: 0, unpaid: 0 });
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [error, setError] = useState("");

  async function fetchDashboard(signal) {
    setLoading(true);
    setError("");
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_API || "").trim();
      if (!apiBase) throw new Error("Missing REACT_APP_BACKEND_API");
      const base = apiBase.replace(/\/$/, "");

      if (!token) throw new Error("Missing auth token");

      const policyRes = await fetch(`${base}/referrals/policy`, {
        signal,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (policyRes.ok) {
        const p = await policyRes.json();
        setPolicy(p);
      } else {
        setPolicy(null);
      }

      const res = await fetch(
        `${base}/referrals/dashboard`,
        {
          signal,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error(`Failed to load dashboard (${res.status})`);
      const data = await res.json();

      setPromoCode(String(data.promoCode || ""));
      setSummary(data.summary || { totalEarnings: 0, paidOut: 0, unpaid: 0 });
      setReferrals(Array.isArray(data.referrals) ? data.referrals : []);
      setLastUpdated(new Date());
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.error(e);
      setError(e?.message || "Failed to load dashboard");
      setPromoCode("");
      setSummary({ totalEarnings: 0, paidOut: 0, unpaid: 0 });
      setReferrals([]);
    } finally {
      if (signal?.aborted) return;
      setLoading(false);
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    fetchDashboard(ac.signal);
    return () => ac.abort();
  }, [token]);

  function copyPromo() {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(promoCode);
    } else {
      const el = document.createElement("input");
      el.value = promoCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }

  return (
    <div className="referral-body">
      <div className="referral-header-wrapper">
        <div className="referral-header-subheader">
          <h1 className="referral-header">Referral Dashboard</h1>
          <h2 className="referral-subheader">{user?.username || user?.user_name || "—"}</h2>
        </div>
        <div className="refresh-wrapper">
          <button
            onClick={() => {
              const ac = new AbortController();
              fetchDashboard(ac.signal);
            }}
            className="referral-refresh-button"
            aria-label="Refresh dashboard"
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <div className="last-updated">Last updated: {formatDateTime(lastUpdated)}</div>
        </div>
      </div>

      {error ? (
        <div className="referral-error" role="alert" aria-live="polite">
          {error}
        </div>
      ) : null}

      <section className="promo-code-section">
        <div className="promo-code-wrapper">
          <h2 className="promo-code-header">Your Promo Code</h2>
          <button
            onClick={copyPromo}
            className="promo-code-copy-button"
            aria-label="Copy promo code"
            disabled={!promoCode}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="promo-code">
          {promoCode || "—"}
        </div>
      </section>

      {policy ? (
        <section className="referral-how-it-works">
          <h2 className="referral-how-header">How Earnings Work</h2>
          <li className="referral-how-text">
            Share your promo code — new subscribers get{" "}
            {fmtCurrency(policy?.discounts?.annualFirstChargeAmountOffDollars, cur)} off their first year.
          </li>
          <li className="referral-how-text">
            {" "}You earn{" "}
            {fmtCurrency(policy?.writerPlus?.annual, cur)} per annual referral.
          </li>
          {policy?.payout?.note ? (
            <p className="referral-payout-note">{policy.payout.note}</p>
          ) : null}
        </section>
      ) : null}

      <section className="earnings-section">
        <SummaryCard label="Total Earnings" value={fmtCurrency(summary.totalEarnings, cur)} loading={loading} />
        <SummaryCard label="Paid Out" value={fmtCurrency(summary.paidOut, cur)} loading={loading} />
        <SummaryCard label="Unpaid" value={fmtCurrency(summary.unpaid, cur)} loading={loading} />
      </section>

      <section className="referral-table-section">
        <h2 className="referral-table-header">Referred Users</h2>
        <div className="referral-table-body">
          <table className="referral-table">
            <colgroup>
              <col className="column-name" />
              <col className="column-join-date" />
              <col className="column-earnings" />
            </colgroup>
            <thead>
              <tr className="referral-table-head">
                <th className="header-cell">Name</th>
                <th className="header-cell">Join Date</th>
                <th className="header-cell header-cell--no-right">Earnings</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="row--loading">
                  <td className="cell cell--roomy">Loading…</td>
                  <td className="cell cell--roomy"></td>
                  <td className="cell cell--roomy cell--no-right"></td>
                </tr>
              )}
              {!loading && referrals.map((r) => (
                <tr key={r.id} className="referral-table-key">
                  <td className="cell cell--emphasis">{r.name}</td>
                  <td className="cell">{fmtDate(r.joined)}</td>
                  <td className="cell cell--no-right">{fmtCurrency(r.earnings, cur)}</td>
                </tr>
              ))}
              {!loading && referrals.length === 0 && (
                <tr>
                  <td colSpan={3} className="no-referrals">No referrals yet — share your promo code to earn.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SummaryCard({ label, value, loading }) {
  return (
    <div className="border border-gray-200 rounded-2xl p-4 bg-white shadow-sm">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{loading ? "…" : value}</div>
    </div>
  );
}

// ---- helpers ----
function fmtCurrency(n, currency = "CAD") {
  if (n == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(Number(n));
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isFinite(d) ? d.toLocaleDateString() : iso;
}

function formatDateTime(d) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return d?.toLocaleString?.() ?? "";
  }
}
